import {
  brandDuotoneLooks,
  type ClipColor,
  COLOR_ADJUSTMENT_LABELS,
  COLOR_ADJUSTMENT_MIN,
  COLOR_ADJUSTMENTS,
  COLOR_LOOKS,
  type ColorAdjustment,
  colorFromLook,
  isNeutralColor,
  normalizeColor,
  type OverlayClip,
  type VideoClip,
  vignetteCss,
} from "@cva/shared";
import { ChevronDown, CopyCheck, RotateCcw, Save, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { useEnv } from "../env";
import { useLibraryActions, useLibraryItems } from "../library";
import { NameDialog } from "../NameDialog";
import { useEditor, useEditorApi } from "../store";
import { IconButton, Row, Section, Slider } from "../ui";
import { colorFilterUrl } from "./color-filters";
import { applyColorEverywhere, setClipColor } from "./commands";

/**
 * Colour correction of a clip or an image: a strip of looks (the own picture
 * with each look), the organization's saved looks, fine-tuning sliders,
 * saving the current values as a look and applying them everywhere.
 */

interface LookOption {
  id: string;
  label: string;
  values: Partial<ClipColor>;
  savedId?: string;
}

function LookTile({
  option,
  thumbnail,
  active,
  onClick,
  onDelete,
}: {
  option: LookOption;
  thumbnail: string | null;
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
}) {
  const filter = colorFilterUrl(normalizeColor(option.values)) ?? undefined;
  const vignette = vignetteCss(option.values.vignette ?? 0);
  return (
    <div className="group relative w-16 shrink-0">
      <button
        type="button"
        onClick={onClick}
        title={option.label}
        className={cn(
          "block w-16 overflow-hidden rounded-md border-2 text-left",
          active ? "border-brand-500" : "border-transparent hover:border-brand-300",
        )}
      >
        <span className="relative block h-16 w-full bg-zinc-800">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              draggable={false}
              loading="lazy"
              className="absolute inset-0 size-full object-cover"
              style={{ filter }}
            />
          ) : (
            <span
              className="absolute inset-0"
              style={{
                background: "linear-gradient(135deg, #f59e0b, #10b981 45%, #3b82f6)",
                filter,
              }}
            />
          )}
          {vignette ? <span className="absolute inset-0" style={{ background: vignette }} /> : null}
        </span>
        <span className="block truncate px-0.5 pt-0.5 text-[10px] text-zinc-600">
          {option.label}
        </span>
      </button>
      {onDelete ? (
        <button
          type="button"
          title="Look löschen"
          onClick={onDelete}
          className="absolute top-0.5 right-0.5 hidden rounded bg-white/90 p-0.5 text-zinc-600 shadow-sm hover:text-red-600 group-hover:block"
        >
          <Trash2 className="size-3" />
        </button>
      ) : null}
    </div>
  );
}

/** Colour of a main track or overlay clip, with "Auf alle Clips". */
export function ColorSection({
  clip,
  track,
}: {
  clip: VideoClip | OverlayClip;
  track: "video" | "overlay";
}) {
  const store = useEditorApi();
  const env = useEnv();
  const asset = env.assets.get(clip.assetId);
  const thumbnail = asset?.thumbnailUrl ?? (clip.kind === "image" ? (asset?.url ?? null) : null);
  const update = (next: ClipColor | undefined, merge?: string) =>
    setClipColor(store, track, clip.id, next, merge);
  const applyEverywhere = () => {
    const count = applyColorEverywhere(store, clip.color);
    toast.success(`Farbe auf ${count} ${count === 1 ? "Clip" : "Clips"} übertragen`);
  };
  return (
    <LookSection
      title="Farbe & Look"
      color={clip.color}
      thumbnail={thumbnail}
      onChange={update}
      applyAll={{
        label: "Auf alle Clips",
        title: "Dieselbe Farbe für alle Clips des Videos",
        run: applyEverywhere,
      }}
    />
  );
}

/**
 * Looks and sliders for any colour correction (clips, image elements). The
 * owner stores the values; `onChange(undefined)` resets.
 */
export function LookSection({
  title,
  color: current,
  thumbnail,
  onChange: update,
  applyAll,
}: {
  title: string;
  color: ClipColor | undefined;
  thumbnail: string | null;
  onChange: (next: ClipColor | undefined, merge?: string) => void;
  applyAll?: { label: string; title: string; run: () => void };
}) {
  const env = useEnv();
  const saved = useLibraryItems(env.orgId, "look");
  const actions = useLibraryActions(env.orgId);
  const brand = useEditor((s) => s.brand);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const color = normalizeColor(current);
  const setValue = (key: ColorAdjustment, value: number) => {
    const next: ClipColor = { ...color, [key]: value };
    // Changed by hand: no longer exactly the look.
    delete next.look;
    update(next, key);
  };
  const options: LookOption[] = [
    ...saved.items.map((item) => ({
      id: `saved:${item.id}`,
      label: item.name,
      values: item.data.values,
      savedId: item.id,
    })),
    ...brandDuotoneLooks(brand?.colors ?? []).map((look) => ({ ...look })),
    ...COLOR_LOOKS.map((look) => ({ id: look.id, label: look.label, values: look.values })),
  ];
  const activeId = current?.look ?? (isNeutralColor(current) ? "original" : null);
  return (
    <Section
      title={title}
      actions={
        isNeutralColor(current) ? null : (
          <IconButton label="Zurücksetzen" onClick={() => update(undefined)}>
            <RotateCcw />
          </IconButton>
        )
      }
    >
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {options.map((option) => (
          <LookTile
            key={option.id}
            option={option}
            thumbnail={thumbnail}
            active={activeId === option.id}
            onClick={() => update(colorFromLook(option))}
            {...(option.savedId ? { onDelete: () => actions.remove.mutate(option.savedId!) } : {})}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-xs font-medium text-zinc-600 hover:text-zinc-900"
      >
        Feinabstimmung
        <ChevronDown className={cn("size-3.5 transition", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="space-y-2">
          {COLOR_ADJUSTMENTS.map((key) => (
            <Row key={key}>
              <span className="w-20 shrink-0 text-xs text-zinc-500">
                {COLOR_ADJUSTMENT_LABELS[key]}
              </span>
              <Slider
                value={color[key]}
                min={COLOR_ADJUSTMENT_MIN[key]}
                max={1}
                step={0.01}
                onChange={(value) => setValue(key, value)}
              />
              <button
                type="button"
                title="Auf 0 setzen"
                onDoubleClick={() => setValue(key, 0)}
                className="w-9 shrink-0 text-right text-xs text-zinc-600 tabular-nums"
              >
                {color[key] > 0 && COLOR_ADJUSTMENT_MIN[key] < 0 ? "+" : ""}
                {Math.round(color[key] * 100)}
              </button>
            </Row>
          ))}
        </div>
      ) : null}
      <Row>
        <button
          type="button"
          disabled={isNeutralColor(current)}
          onClick={() => setSaving(true)}
          className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 [&_svg]:size-3.5"
          title="Die aktuellen Werte als eigenen Look speichern – für alle im Team"
        >
          <Save /> Als Look speichern
        </button>
        {applyAll ? (
          <button
            type="button"
            onClick={applyAll.run}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-3.5"
            title={applyAll.title}
          >
            <CopyCheck /> {applyAll.label}
          </button>
        ) : null}
      </Row>
      <NameDialog
        open={saving}
        onOpenChange={setSaving}
        title="Look speichern"
        description="Der Look erscheint in der Leiste oben – bei Clips und Bildern, für alle im Team."
        initial=""
        pending={actions.save.isPending}
        onConfirm={async (name) => {
          const { look: _look, ...values } = normalizeColor(current);
          const item = await actions.save.mutateAsync({ kind: "look", name, data: { values } });
          update({ ...normalizeColor(current), look: `saved:${item.id}` });
        }}
      />
    </Section>
  );
}
